import React, { useCallback } from 'react';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import {
  READ_EMPLOYEE_CONTRACTS,
  READ_EMPLOYEE_COMMUNICATIONS,
  READ_EMPLOYEE_COMPENSATION_PACKAGES,
  READ_EMPLOYEE_TIMELINE,
  READ_EMPLOYEE_MAIN_PERSONAL_INFO,
  READ_EMPLOYEE_WORKING_STATUS,
  READ_EMPLOYEE_QUALIFICATIONS,
  READ_LIST_EMPLOYEE_OFFER,
  READ_LIST_EMPLOYEE_PERFORMANCE,
  useEmployeeUrl,
  EMPLOYEE_ROUTE_PATHS,
} from 'domain/shared/employee';
import { FEEDBACKS_ABOUT_ME } from 'domain/people/employee/feedbacks/EmployeeFeedbacksService';

import { READ_ACCESS_LOG } from 'domain/people/employee/AccessLog/accessLogService';
import { READ_EMPLOYEE_ASSIGNMENTS } from 'domain/shared/assignment';
import { READ_PERFORMANCE_REPORT } from 'domain/people/employee/performanceReport/usePerformanceReportService';
import { READ_EMPLOYEE_COMPANY_ROLES } from '../CareerCapabilities/companyRoles/shared/companyRolesService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthorization } from 'framework/authorization';
import { EMPLOYEE_CONTEXT } from '../EmployeeContext';
import { useLeavesBalancesUrl } from '../leavebalance/LeaveBalancesRouter';
import { READ_EMPLOYEE_SKILLS } from '../skills/useEmployeeSkillsService';
import { TabItem, TabsWrapper } from 'framework/ui/TabsWrapper';
import { READ_EMPLOYEE_DOCUMENTS } from '../empoyeeDocuments/constants';
import { READ_COMPLIANCE_REGISTRY_DOCUMENTS } from 'domain/shared/compliance';
import { PEOPLE_MANAGEMENT_LEAVE_BALANCE_READ } from '../leavebalance/shared/constants';
const useEmployeeTabs = (): TabItem[] => {
  const {
    getSingleEntityUrl,
    getTimelineUrl,
    getAccessLogUrl,
    getEmployeeAssignmentsUrl,
    getEmployeePerformanceReportUrl,
    getEmployeeComplianceUrl,
  } = useEmployeeUrl();
  const navigate = useNavigate();
  const { getListUrl } = useLeavesBalancesUrl();
  const employeeUrl = getSingleEntityUrl();

  const { isFeatureEnabled } = useFeatureToggle();
  const { isGranted: globalIsGranted } = useAuthorization();
  const isGranted = useCallback(
    (permission: string | string[]) => globalIsGranted(permission, EMPLOYEE_CONTEXT),
    [globalIsGranted],
  );

  const onChangeTabCallback = useCallback(
    (route: string) => {
      navigate(route, { preventScrollReset: true });
    },
    [navigate],
  );

  return [
    {
      label: 'Timeline',
      onClick: () => onChangeTabCallback(getTimelineUrl()),
      route: getTimelineUrl(),
      isVisible: isGranted(READ_EMPLOYEE_TIMELINE),
    },
    {
      label: 'Personal & Legal',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.PERSONAL_LEGAL}`,
      isVisible: isGranted(READ_EMPLOYEE_MAIN_PERSONAL_INFO) || isGranted(READ_EMPLOYEE_WORKING_STATUS),
    },
    {
      label: 'Career & Capabilities',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}`,
      isVisible: isGranted([
        READ_EMPLOYEE_SKILLS,
        READ_EMPLOYEE_QUALIFICATIONS,
        READ_EMPLOYEE_COMMUNICATIONS,
        READ_LIST_EMPLOYEE_PERFORMANCE,
        READ_EMPLOYEE_COMPANY_ROLES,
      ]),
    },
    {
      label: 'Compensation',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.COMPENSATION}`,
      isVisible: isGranted(READ_EMPLOYEE_COMPENSATION_PACKAGES),
    },
    {
      label: 'Contracts',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.CONTRACTS}`,
      isVisible: isGranted(READ_EMPLOYEE_CONTRACTS),
    },
    {
      label: 'Leaves',
      route: getListUrl(),
      isVisible: isGranted(PEOPLE_MANAGEMENT_LEAVE_BALANCE_READ),
    },
    {
      label: 'Assignments',
      route: getEmployeeAssignmentsUrl(),
      isVisible: isGranted(READ_EMPLOYEE_ASSIGNMENTS),
    },
    {
      label: 'Documents',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.DOCUMENTS}`,
      isVisible: isGranted(READ_EMPLOYEE_DOCUMENTS),
    },
    {
      label: 'Feedback',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.FEEDBACKS}`,
      isVisible: isGranted(FEEDBACKS_ABOUT_ME),
    },
    {
      label: 'Offers',
      route: `${employeeUrl}/${EMPLOYEE_ROUTE_PATHS.OFFERS}`,
      isVisible: isFeatureEnabled(Features.EMPLOYEE_OFFERS) && isGranted(READ_LIST_EMPLOYEE_OFFER),
    },
    {
      label: 'Access Log',
      route: getAccessLogUrl(),
      isVisible: isGranted(READ_ACCESS_LOG),
    },
    {
      label: 'Performance Report',
      route: getEmployeePerformanceReportUrl(),
      isVisible: isGranted(READ_PERFORMANCE_REPORT),
    },
    {
      label: 'Compliance',
      route: getEmployeeComplianceUrl(),
      isVisible: isGranted(READ_COMPLIANCE_REGISTRY_DOCUMENTS),
    },
  ].map(item => {
    return {
      onClick: () => onChangeTabCallback(item.route),
      value: item.route,
      ...item,
    };
  });
};

const EmployeeTabsMenu = () => {
  const location = useLocation();
  const { isGranted } = useAuthorization();
  const options = useEmployeeTabs();

  const hasTimelineAccess = isGranted(READ_EMPLOYEE_TIMELINE, EMPLOYEE_CONTEXT);
  const currentTab = options.find(({ value }) => location.pathname.includes(String(value)))?.value;

  const getTabByLabel = (label: string) => options.find(option => option.label === label)?.value;

  const fallbackTab = hasTimelineAccess ? getTabByLabel('Timeline') : getTabByLabel('Personal & Legal');

  const isLegacyProfileRoute = location.pathname.includes('/profile');

  const resolvedTab = isLegacyProfileRoute ? getTabByLabel('Personal & Legal') : currentTab;

  const defaultTab = resolvedTab ?? fallbackTab;
  return (
    <TabsWrapper
      defaultTab={defaultTab}
      sxContentProps={{}}
      isShowContent={false}
      sxTabs={{
        overflow: 'auto',
        minHeight: 'auto',
      }}
      sxTab={{
        minHeight: '40px',
        minWidth: 'auto',
        whiteSpace: 'nowrap',
        padding: '12px 8px',
      }}
      tabs={options}
    />
  );
};

export default EmployeeTabsMenu;
